// Access token lives in memory only; the refresh token is kept in localStorage (v1 decision,
// STAGE_1 1.9) so a reload or a new tab can restore the session.

const REFRESH_KEY = 'sos.refreshToken';
// The vendor working as somebody else (STAGE_6 6.7): that user's access token, kept for
// this tab only. The vendor's own refresh token stays where it is, so ending it - or the
// token running out - puts the vendor back in their own session.
const IMPERSONATION_KEY = 'sos.impersonation';

export interface TokenPair {
  access_token: string;
  refresh_token: string;
}

type Listener = () => void;

let accessToken: string | null = null;
const clearListeners = new Set<Listener>();

function readRefresh(): string | null {
  try {
    return window.localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

function writeRefresh(value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(REFRESH_KEY);
    else window.localStorage.setItem(REFRESH_KEY, value);
  } catch {
    // Storage unavailable (private mode): the session lasts until the page is closed.
  }
}

function readImpersonation(): string | null {
  try {
    return window.sessionStorage.getItem(IMPERSONATION_KEY);
  } catch {
    return null;
  }
}

export const tokenStore = {
  REFRESH_KEY,

  getAccess(): string | null {
    return readImpersonation() ?? accessToken;
  },

  isImpersonating(): boolean {
    return readImpersonation() !== null;
  },

  startImpersonation(token: string): void {
    try {
      window.sessionStorage.setItem(IMPERSONATION_KEY, token);
    } catch {
      // Storage unavailable: signing in as somebody needs it, so nothing happens.
    }
  },

  endImpersonation(): void {
    try {
      window.sessionStorage.removeItem(IMPERSONATION_KEY);
    } catch {
      // Nothing was stored.
    }
  },

  getRefresh(): string | null {
    return readRefresh();
  },

  set(pair: TokenPair): void {
    accessToken = pair.access_token;
    writeRefresh(pair.refresh_token);
  },

  /** Drops both tokens. `notify` tells subscribers the session ended unexpectedly. */
  clear(notify = false): void {
    accessToken = null;
    writeRefresh(null);
    tokenStore.endImpersonation();
    if (notify) clearListeners.forEach((listener) => { listener(); });
  },

  /** Forget the in-memory access token only (another tab rotated the refresh token). */
  dropAccess(): void {
    accessToken = null;
  },

  onSessionLost(listener: Listener): () => void {
    clearListeners.add(listener);
    return () => clearListeners.delete(listener);
  },
};
